import { T, useGT } from 'gt-react';
import { MicrosoftExcelLogoIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { toast } from 'src/components/ui/toast';
import { cn } from '@/lib/utils';
import type { FileExportSummary } from 'src/api/rpc/hooks/procurement/useProcurementExports';

/**
 * "Export to Excel" for a procurement screen. The file is built by a
 * background job over the screen's full filtered result set and downloaded
 * straight from storage, so the button only shows a pending state while it
 * works — the rest of the page stays usable.
 */
export function ExcelExportButton({
  onExport,
  isPending,
  disabled,
  className,
  compact,
}: {
  /** Starts the export; resolves once the download has been triggered. */
  onExport: () => Promise<FileExportSummary>;
  isPending: boolean;
  disabled?: boolean;
  className?: string;
  /** Small "Export" button for sitting inline beside a row count. */
  compact?: boolean;
}) {
  const gt = useGT();

  async function handleClick() {
    try {
      const file = await onExport();
      if (file.truncated) {
        toast.warning(
          gt(
            'Exported the first {count} rows. Narrow the filters to export the rest.',
            {
              context: 'procurement excel export truncated toast',
              count: file.rowCount.toLocaleString(),
            },
          ),
        );
      } else {
        toast.success(
          gt('Exported {count} rows to Excel.', {
            context: 'procurement excel export success toast',
            count: file.rowCount.toLocaleString(),
          }),
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : gt('Excel export failed.', {
              context: 'procurement excel export error toast',
            }),
      );
    }
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => void handleClick()}
      disabled={disabled || isPending}
      className={cn(
        'whitespace-nowrap',
        compact ? 'h-6 gap-1 px-2 text-xs' : 'h-9',
        className,
      )}
    >
      <MicrosoftExcelLogoIcon
        className={compact ? 'h-3.5 w-3.5' : 'mr-1 h-4 w-4'}
      />
      {isPending ? (
        <T context="procurement excel export button pending">Exporting…</T>
      ) : compact ? (
        <T context="procurement excel export button compact">Export</T>
      ) : (
        <T context="procurement excel export button">Export to Excel</T>
      )}
    </Button>
  );
}
